import React from "react"
import { Alert, FormGroup, Input, Label } from "reactstrap"
import Select from "react-select"
import { ForeignKeyInput } from "./ForeignKeyComponents"

function CustomColumnInput({ column, value, onChange, setError, inputId, maxLength = 200, autoFocus = false }) {
    if (!column) return null

    const clearError = () => {
        if (setError) setError(null)
    }

    if (column.columnType === "MIRROR") {
        return <Alert color="info" className="mb-0">Mirror columns are read-only and update from their parent FK column.</Alert>
    }

    if (column.columnType === "FK_OBJECT") {
        return <ForeignKeyInput model={column.contentType?.model} defaultValue={value} onChange={onChange} />
    }

    if (column.columnType === "ENUM") {
        const options = (column.allowedOptions || []).map((opt) => ({ value: opt, label: opt }))
        return (
            <Select
                options={options}
                value={value ? { value, label: value } : null}
                onChange={(selected) => {
                    clearError()
                    onChange(selected ? selected.value : null)
                }}
                isClearable
                autoFocus={autoFocus}
            />
        )
    }

    if (column.columnType === "BOOLEAN") {
        const currentValue = value
        const isRequired = !!column.isRequired
        const isUnset = currentValue === null || currentValue === "" || currentValue === undefined
        const showToggle = isRequired || !isUnset
        const isChecked = currentValue === "true" || currentValue === true

        return (
            <div>
                <FormGroup check style={{ marginBottom: "8px", textAlign: "left" }}>
                    <Input
                        type="checkbox"
                        id={`${inputId}-enabled`}
                        checked={isRequired || !isUnset}
                        disabled={isRequired}
                        onChange={(e) => {
                            clearError()
                            onChange(e.target.checked ? "false" : null)
                        }}
                    />
                    <Label check for={`${inputId}-enabled`}>
                        Set a value
                    </Label>
                </FormGroup>
                {showToggle ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "left", gap: "8px" }}>
                        <span>Yes</span>
                        <div className="toggle-switch">
                            <input
                                type="checkbox"
                                className="toggle-switch-checkbox"
                                name={inputId}
                                id={inputId}
                                checked={!isChecked}
                                onChange={() => {
                                    clearError()
                                    onChange(String(!isChecked))
                                }}
                            />
                            <label className="toggle-switch-label" htmlFor={inputId}>
                                <span className="toggle-switch-inner" />
                                <span className="toggle-switch-switch" />
                            </label>
                        </div>
                        <span>No</span>
                    </div>
                ) : (
                    <div className="text-muted small mt-1">Value not set.</div>
                )}
            </div>
        )
    }

    if (column.columnType === "URL") {
        return (
            <Input
                autoComplete="off"
                type="url"
                maxLength={maxLength || 500}
                name={inputId}
                id={inputId}
                value={value || ""}
                placeholder="https://..."
                autoFocus={autoFocus}
                onChange={(e) => {
                    clearError()
                    onChange(e.target.value)
                }}
            />
        )
    }

    if (column.columnType === "DATETIME") {
        return (
            <Input
                autoComplete="off"
                type="datetime-local"
                maxLength={maxLength}
                name={inputId}
                id={inputId}
                value={value || ""}
                autoFocus={autoFocus}
                onChange={(e) => {
                    clearError()
                    onChange(e.target.value)
                }}
            />
        )
    }

    if (column.columnType === "TIME") {
        return (
            <Input
                autoComplete="off"
                type="time"
                name={inputId}
                id={inputId}
                value={value || ""}
                autoFocus={autoFocus}
                onChange={(e) => {
                    clearError()
                    onChange(e.target.value)
                }}
            />
        )
    }

    if (column.columnType === "DATE") {
        return (
            <Input
                autoComplete="off"
                type="date"
                maxLength={maxLength}
                name={inputId}
                id={inputId}
                value={value || ""}
                autoFocus={autoFocus}
                onChange={(e) => {
                    clearError()
                    onChange(e.target.value)
                }}
            />
        )
    }

    if (column.columnType === "NUMBER") {
        return (
            <Input
                autoComplete="off"
                type="number"
                maxLength={maxLength}
                name={inputId}
                id={inputId}
                value={value || ""}
                autoFocus={autoFocus}
                onChange={(e) => {
                    const val = e.target.value
                    if (val && val.includes(".")) {
                        if (setError) setError("Only whole numbers are allowed for this field.")
                        return
                    }
                    clearError()
                    onChange(val)
                }}
            />
        )
    }

    if (column.columnType === "DECIMAL") {
        return (
            <Input
                autoComplete="off"
                type="number"
                step="any"
                maxLength={maxLength}
                name={inputId}
                id={inputId}
                value={value || ""}
                autoFocus={autoFocus}
                onChange={(e) => {
                    clearError()
                    onChange(e.target.value)
                }}
            />
        )
    }

    return (
        <Input
            autoComplete="off"
            type={column.columnType || "text"}
            maxLength={maxLength}
            name={inputId}
            id={inputId}
            value={value || ""}
            autoFocus={autoFocus}
            onChange={(e) => {
                clearError()
                onChange(e.target.value)
            }}
        />
    )
}

export default CustomColumnInput
